<template>
	<select
		v-bind="$attrs"
		:id="context.id"
		v-model="idref"
		class="formkit-input"
		:name="context.node.name"
		:disabled="context.disabled as boolean"
	>
		<option v-for="option in options" :key="option.value" :value="option.value">
			{{ option.label }}
		</option>
	</select>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { FormKitFrameworkContext } from "@formkit/core";
import { selectPlaceholder } from "@/composables/useXsdToFormKit";

type Option = { value?: string; label?: string };

const props = defineProps<{
	context: FormKitFrameworkContext;
}>();

const idref = ref<string | undefined>();
watch(idref, async (newIDREF, oldIDREF) => {
	if (newIDREF === oldIDREF) {
		return;
	}

	if (!newIDREF) {
		await props.context.node.input(undefined);
		return;
	}

	await props.context.node.input({
		IDREF: newIDREF,
	});
});

const idrefObj = computed(() => props.context.value);
watch(
	idrefObj,
	(newIDREF, oldIDREF) => {
		if (newIDREF?.IDREF === oldIDREF?.IDREF) {
			return;
		}

		idref.value = newIDREF?.IDREF;
	},
	{ immediate: true, deep: true },
);

const typeToSearch = computed(() => {
	if (!props.context.node.props?.attrs) return "";

	const xsdType = props.context.node.props?.attrs?.["data-xsd"];
	return xsdType?.substring(3, xsdType.length - 4);
});

const options = ref<Option[]>([]);
const rootNode = computed(() => props.context.node.root);

watch(
	rootNode,
	(rootNode) => {
		rootNode.on("mounted", () => {
			options.value = getOptions();
		});
	},
	{ immediate: true },
);

function getOptions() {
	const idrefNode = rootNode.value.at(typeToSearch.value);
	if (!idrefNode) {
		return [];
	}

	return [{ value: undefined, label: selectPlaceholder } as Option].concat(
		idrefNode.children
			.filter(
				(instanceNode) =>
					typeof instanceNode.value === "object" &&
					instanceNode.value &&
					"ID" in instanceNode.value,
			)
			.map((instanceNode) => {
				// @ts-expect-error: we assume that ID has a value of string
				const idValue: string | undefined = instanceNode.value.ID;

				return {
					value: idValue,
					label:
						"context" in instanceNode ? instanceNode.context?.label : idValue,
				};
			}),
	);
}

watch(
	() => rootNode.value.context?.value[typeToSearch.value],
	() => {
		options.value = getOptions();
	},
	{ immediate: true, deep: true },
);
</script>
